/// <reference path="../types.d.ts" />
import { createReducer } from '@reduxjs/toolkit';
import InitialState from '../reducers/initial-state';
import * as UIActions from '../actions/ui';
import { configAvailable, fetchCaseListColumnConfiguration, setBackToWorklistPath, setHomepagePreference, setPropertyConfig, logout } from '../actions/ui';
import { NAV_OPTIONS, retrieveGroups } from '../core/navigation';
import { displayContexts, jumpContext, tokenAvailable } from '../actions/contexts.action';
import { checkResources } from '../core/resources';
import { shortcuts as ShortcutSet } from './keypress/shortcuts';
import { commonEnums, AmplifyCore, objectUtils, uiActions, appServiceLines } from '@codametrix/ui-common';
import { buildRoleSet } from './users/roles';
import * as ServiceLineActions from '../actions/service-lines';
const { SystemRole } = commonEnums;
const { PermissionLevel } = commonEnums;
const getAdminTabs = (actionGroups, activeContext, serviceLineName)=>{
    const adminActionGroups = actionGroups.find((actionGroup)=>{
        return actionGroup.key === 'admin';
    });
    if (adminActionGroups === undefined) {
        return [];
    }
    return adminActionGroups.options[0].subActions.filter((tab)=>{
        return tab.enabled;
    }).filter((tab)=>{
        const resourceForTab = activeContext ? AmplifyCore.benchEditable.isResourceAllowed(activeContext, tab.resourceId, serviceLineName) : false;
        return resourceForTab;
    }).map((tab)=>{
        const resourceForTab = activeContext?.resources.find((resource)=>{
            return resource.resourceName === tab.resourceId;
        });
        return {
            ...tab,
            permissionLevel: resourceForTab?.permissionLevel || PermissionLevel.DENY
        };
    }) || [];
};
const highlightChanges = (state, locationChange)=>{
    // Clone before _highlightChanges mutates: it must not touch the Immer draft and also return a
    // new root object (@@router/LOCATION_CHANGE) — mutating draft + returning violates Immer.
    const actionGroups = objectUtils.cloneDeep(state.actionGroups);
    return {
        ...state,
        actionGroups: _highlightChanges(actionGroups, locationChange.location.pathname)
    };
};
const _highlightChanges = (actionGroups, pathname)=>{
    actionGroups.forEach((group)=>{
        group.options.forEach((opt)=>{
            if (!opt.path) {
                opt.active = false;
            } else {
                opt.active = pathname.indexOf(opt.path) !== -1;
            }
        });
    });
    return actionGroups;
};
// modify action groups based on configured feature flags
const configureActions = (actionGroups, config)=>{
    actionGroups.map((group)=>{
        return group.options.map((option)=>{
            if (option?.subActions?.length) {
                //set the first enabled subAction as the active subaction and default option
                const activeSubaction = option.subActions.findIndex((action)=>action.enabled);
                if (activeSubaction !== -1) {
                    option.subActions[activeSubaction].active = true;
                } else {
                    option.enabled = false;
                }
            }
            return option;
        });
    });
    return actionGroups;
};
const getPHIMask = (roleSet)=>{
    if (roleSet.has(SystemRole.DEMO)) {
        return {
            available: true,
            active: true
        };
    }
    return {
        available: false,
        active: false
    };
};
const filterShortcutBindings = (shorcutBindings, roleSet)=>{
    const shortcuts = shorcutBindings.filter((shortcut)=>shortcut.roles.some((r)=>roleSet.has(r)));
    return shortcuts;
};
const handleContextChoice = (state, rawContext)=>{
    const jwtToken = !rawContext.jwtToken && state?.context?.jwtToken ? state.context.jwtToken : rawContext.jwtToken;
    const contexts = rawContext.contexts.map((ctx)=>{
        const resourceMap = new Map();
        ctx.resources.forEach((resource)=>{
            resourceMap.set(resource.resourceId ?? -1, resource);
        });
        const filtered = ctx.resources.filter((resource)=>resource.parentId === null || resourceMap.has(resource.parentId));
        return {
            ...ctx,
            resources: filtered
        };
    });
    const context = {
        ...rawContext,
        jwtToken,
        contexts
    };
    // ACTION GROUPS & ADMIN TABS //
    // MOVED TO saveServiceLine //
    // ROLES & PERMISSIONS //
    const roleSet = new Set(buildRoleSet(context));
    const phiMask = getPHIMask(roleSet);
    const shortcuts = filterShortcutBindings(ShortcutSet, roleSet);
    const tenantId = context.activeContext?.tenantId;
    const { activeUser } = state;
    const org = activeUser.roles.find((persona)=>persona.tenantId === tenantId);
    const roles = org ? org.roles : [];
    const serviceLine = AmplifyCore.isIPCServiceline(state.serviceLine?.name ?? '') ? commonEnums.ServiceLines.BEDSIDE_PRO : state.serviceLine?.name ?? '';
    const hasAmplifyCaseRunV2Access = context.activeContext ? AmplifyCore.benchEditable.isResourceAllowed(context.activeContext, commonEnums.ResourceId.AMPLIFY_CASE_RUN_V2, serviceLine) : false;
    return {
        ...state,
        roles,
        context: {
            ...context,
            expiryTimestamp: Date.now(),
            serviceLineName: state.serviceLine?.name
        },
        phiMask,
        shortcuts,
        hasAmplifyCaseRunV2Access
    };
};
const removeModal = (state)=>{
    const nextState = {
        ...state
    };
    delete nextState.modal;
    return nextState;
};
const getNavOptionByKey = (key)=>{
    return NAV_OPTIONS.find((option)=>{
        return option.key === key;
    });
};
const reducer = createReducer(InitialState.ui, (builder)=>{
    // logout resets to initial state
    builder.addCase(logout, ()=>({
            ...InitialState.ui
        }));
    // uiActions.resetCommonState also resets ui
    builder.addCase(uiActions.resetCommonState.type, ()=>{
        return {
            ...InitialState.ui
        };
    });
    // jumpContext.pending sets awaitingResources: true
    // Note: this replaces BOTH the cmxReducerFactory reset AND the explicit case
    builder.addCase(jumpContext.pending.type, (state)=>{
        return {
            ...state,
            awaitingResources: true
        };
    });
    builder.addCase(UIActions.replaceUIState, (state, action)=>{
        const newState = action.payload;
        const jwtToken = state?.context?.jwtToken ?? newState?.context.jwtToken;
        return {
            ...state,
            ...newState,
            awaitingTokenTransfer: false,
            context: {
                ...newState.context,
                jwtToken
            }
        };
    });
    builder.addCase(uiActions.awaitingResources.type, (state, action)=>{
        return {
            ...state,
            awaitingResources: action.payload
        };
    });
    builder.addCase(uiActions.chooseContext.type, (state, action)=>{
        return handleContextChoice(state, action.payload);
    });
    builder.addCase(displayContexts.type, (state, action)=>{
        return {
            ...state,
            contexts: action.payload,
            loading: false
        };
    });
    builder.addCase(UIActions.loading, (state, action)=>{
        return {
            ...state,
            loading: action.payload
        };
    });
    builder.addCase(UIActions.loadingAction, (state, action)=>{
        return {
            ...state,
            loadingState: action.payload
        };
    });
    builder.addCase(UIActions.dismissModal, removeModal);
    builder.addCase(UIActions.showModalMessage, (state, action)=>({
            ...state,
            modal: action.payload
        }));
    builder.addCase(UIActions.onLocationChange, (state, action)=>{
        return highlightChanges(state, action.payload);
    });
    builder.addCase(UIActions.navigate, (state)=>{
        const actionGroups = state.actionGroups;
        const orignalActionGroups = actionGroups.map((actionGroup)=>{
            return getNavOptionByKey(actionGroup.key);
        });
        const AdminTabs = state.AdminTabs.map((tab, index)=>{
            if (index === 0) {
                return {
                    ...tab,
                    active: true
                };
            } else {
                return {
                    ...tab,
                    active: false
                };
            }
        });
        return {
            ...state,
            orignalActionGroups,
            AdminTabs
        };
    });
    builder.addCase(UIActions.changeAdminTab, (state, action)=>{
        return {
            ...state,
            AdminTabs: action.payload
        };
    });
    builder.addCase(UIActions.detailTogglePHI, (state, action)=>{
        return {
            ...state,
            phiMask: action.payload
        };
    });
    builder.addCase(configAvailable, (state, action)=>{
        const params = action.payload;
        return {
            ...state,
            config: {
                ...state.config,
                mixpanelApiKey: params?.userAnalytics?.enabled ? params?.userAnalytics.apiKey : undefined,
                grid: params?.grid,
                isNewDesign: params?.newDesign?.enabled ?? false,
                apiProxyServiceEnabled: params?.apiProxyServiceEnabled?.enabled ?? false
            }
        };
    });
    builder.addCase(UIActions.setActiveUser, (state, action)=>{
        return {
            ...state,
            activeUser: action.payload
        };
    });
    builder.addCase(ServiceLineActions.chooseServiceLine.pending, (state)=>{
        return {
            ...state,
            loading: true
        };
    });
    builder.addCase(ServiceLineActions.chooseServiceLine.fulfilled, (state)=>{
        return {
            ...state,
            loading: false
        };
    });
    builder.addCase(ServiceLineActions.saveServiceLine, (state, action)=>{
        const choice = action.payload;
        const { config, context } = state;
        let actionGroups;
        let adminTabs;
        const choiceName = choice?.name ?? '';
        if (context.activeContext) {
            const availableActions = retrieveGroups(context.activeContext, choiceName);
            actionGroups = _highlightChanges(availableActions, window.location.pathname);
            actionGroups = configureActions(actionGroups, config);
            adminTabs = getAdminTabs(actionGroups, context.activeContext, choiceName);
        } else {
            actionGroups = state.actionGroups;
            adminTabs = [];
        }
        if (choice === undefined) {
            return {
                ...state
            };
        }
        const serviceLineGroups = actionGroups.map((group)=>{
            if (group.options[0].id === 'cases') {
                return {
                    ...group,
                    options: [
                        {
                            ...group.options[0],
                            path: `cases/${appServiceLines.toServiceLineSlug(choiceName)}/`
                        }
                    ]
                };
            }
            return group;
        });
        // Property config will be set separately via setPropertyConfig action
        const activeContext = state.context.activeContext ? {
            ...state.context.activeContext,
            serviceLineName: choiceName
        } : null;
        const hasAmplifyCaseRunV2Access = state.context.activeContext ? AmplifyCore.benchEditable.isResourceAllowed(state.context.activeContext, commonEnums.ResourceId.AMPLIFY_CASE_RUN_V2, choiceName) : false;
        const hasWorklistMaintenanceWindow = state.context.activeContext ? AmplifyCore.benchEditable.isResourceAllowed(state.context.activeContext, commonEnums.ResourceId.WORKLIST_MAINTENANCE_WINDOW, choiceName) : false;
        return {
            ...state,
            serviceLine: choice,
            actionGroups: [
                ...serviceLineGroups
            ],
            hasAmplifyCaseRunV2Access,
            hasWorklistMaintenanceWindow,
            AdminTabs: [
                ...adminTabs
            ],
            hasResources: checkResources(serviceLineGroups),
            context: {
                ...state.context,
                activeContext,
                serviceLineName: choiceName
            }
        };
    });
    builder.addCase(ServiceLineActions.navigateServiceLine.pending, (state)=>{
        return {
            ...state,
            loading: true
        };
    });
    builder.addCase(ServiceLineActions.navigateServiceLine.fulfilled, (state)=>{
        return {
            ...state,
            loading: false
        };
    });
    builder.addCase(setHomepagePreference, (state, action)=>{
        return {
            ...state,
            homepagePreference: action.payload
        };
    });
    builder.addCase(setPropertyConfig, (state, action)=>{
        return {
            ...state,
            propertyConfig: action.payload
        };
    });
    builder.addCase(setBackToWorklistPath, (state, action)=>{
        return {
            ...state,
            backToWorklistPath: action.payload.path
        };
    });
    builder.addCase(tokenAvailable.type, (state, action)=>{
        return {
            ...state,
            awaitingTokenTransfer: false,
            context: {
                ...state.context,
                jwtToken: action.payload.token ?? undefined
            }
        };
    });
    builder.addCase(fetchCaseListColumnConfiguration.fulfilled, (state, action)=>{
        return {
            ...state,
            caseListColumnConfig: action.payload ?? state.caseListColumnConfig
        };
    });
    builder.addCase(fetchCaseListColumnConfiguration.rejected, (state, action)=>{
        console.error('Failed to fetch column configuration:', action.error);
        return state;
    });
});
export default reducer;
